import { SpinnerGapIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button, ShowMoreButton } from "@/components/ui/button";
import { ItemSkeleton } from "@/components/ui/feedback/skeleton";
import SearchBar from "@/components/ui/molecules/FilterSearchBar";
import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import useNewsFeedFilters from "@/store/news-feed/filters/newsFeed-filter.slice";
import { useAnalysisSidebarFilterParams } from "@/store/news-feed/params/analysis-params.context";
import FilterComponentItem from "../FilterComponentItem";
import {
  buildMediaListHierarchy,
  filterMediaListHierarchy,
  paginateMediaListHierarchy,
} from "./mediaListHierarchy";
import PublisherMediaListGroupRow from "./PublisherMediaListGroupRow";
import { useMediaListFilters } from "./useMediaListFilters";

const CATEGORY: ShareVoiceSidebarCategory = "PUBLISHER_MEDIA_LIST_ID";
const GROUP_CATEGORY: ShareVoiceSidebarCategory =
  "PUBLISHER_MEDIA_LIST_GROUP_ID";
const TOP_LEVEL_PAGE_SIZE = 10;
const SEARCH_THRESHOLD = 10;

const PublisherMediaListFilterContainer = () => {
  const { selectedFilters, toggleFilterSelection } =
    useAnalysisSidebarFilterParams();

  const expandedCategories = useNewsFeedFilters(
    (state) => state.expandedCategories,
  );
  const setCategoryExpansion = useNewsFeedFilters(
    (state) => state.setCategoryExpansion,
  );
  const isExpanded = expandedCategories.has(CATEGORY);

  const {
    listItems,
    listItemsMap,
    groupItems,
    groupItemsMap,
    isLoading,
    error,
    refetch,
  } = useMediaListFilters();

  const [searchTerm, setSearchTerm] = useState("");
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(
    new Set(),
  );
  const [visibleTopCount, setVisibleTopCount] = useState(TOP_LEVEL_PAGE_SIZE);

  useEffect(() => {
    if (error && isExpanded) console.error("Media Lists filter error:", error);
  }, [error, isExpanded]);

  const selectedListIds = new Set(selectedFilters.get(CATEGORY) ?? []);
  const selectedGroupIds = new Set(selectedFilters.get(GROUP_CATEGORY) ?? []);

  const listTotalsById = new Map(
    [...listItemsMap.entries()].map(([id, item]) => [id, item.total]),
  );

  const { listsByGroupId, ungroupedLists } = buildMediaListHierarchy(
    listItems,
    groupItemsMap,
  );
  const { filteredGroups, filteredUngrouped } = filterMediaListHierarchy(
    groupItems,
    listsByGroupId,
    ungroupedLists,
    searchTerm,
  );

  const totalTopLevel = filteredGroups.length + filteredUngrouped.length;
  const { visibleItems } = paginateMediaListHierarchy(
    filteredGroups,
    filteredUngrouped,
    visibleTopCount,
  );
  const hasMoreTopLevel = visibleTopCount < totalTopLevel;
  const canShowLessTopLevel = visibleTopCount > TOP_LEVEL_PAGE_SIZE;
  const showSearch =
    listItems.length + groupItems.length > SEARCH_THRESHOLD ||
    searchTerm.length > 0;
  const hasNoData = listItems.length === 0 && groupItems.length === 0;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setVisibleTopCount(TOP_LEVEL_PAGE_SIZE);
  };
  const handleClearSearch = () => {
    setSearchTerm("");
    setVisibleTopCount(TOP_LEVEL_PAGE_SIZE);
  };

  const toggleGroupExpansion = (groupId: string) =>
    setExpandedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });

  const errorBanner = (
    <div className="text-xxs text-red-500 leading-snug py-0.5 flex items-center gap-1">
      Failed to load media lists.
      <Button
        variant="ghost"
        size="sm"
        onClick={refetch}
        className="h-auto p-0 text-xxs underline"
      >
        Retry
      </Button>
    </div>
  );

  return (
    <Accordion
      type="single"
      collapsible
      value={isExpanded ? CATEGORY : ""}
      onValueChange={(value) =>
        setCategoryExpansion(CATEGORY, value === CATEGORY)
      }
    >
      <AccordionItem value={CATEGORY} className="px-2">
        <AccordionTrigger
          size="sm"
          className="font-medium text-xs text-gray-700 py-2"
        >
          <span className="flex items-center gap-1">
            Media Lists
            {isLoading && <SpinnerGapIcon size={12} className="animate-spin" />}
          </span>
        </AccordionTrigger>
        <AccordionContent className="pb-2">
          {isExpanded && !!error && hasNoData ? (
            errorBanner
          ) : isExpanded && isLoading ? (
            <ItemSkeleton
              numberOfItems={3}
              showNestedItems={true}
              nestedItemsCount={2}
            />
          ) : isExpanded && !isLoading && hasNoData && !error ? (
            <p className="text-[10px] text-slate-500 ml-2">
              Media lists aren&apos;t yet available in your account.
            </p>
          ) : (
            <>
              {error && errorBanner}
              {showSearch && (
                <div className="sticky top-0 bg-white">
                  <SearchBar
                    isOpen={isExpanded}
                    searchTerm={searchTerm}
                    onSearchChange={handleSearchChange}
                    onClearSearch={handleClearSearch}
                    title="media lists"
                  />
                </div>
              )}
              <div className="space-y-1">
                {visibleItems.map((item) =>
                  item.category === GROUP_CATEGORY ? (
                    <PublisherMediaListGroupRow
                      key={`group-${item.id}`}
                      group={item}
                      total={groupItemsMap.get(item.id)?.total}
                      isGroupSelected={selectedGroupIds.has(item.id)}
                      isExpanded={expandedGroupIds.has(item.id)}
                      memberLists={listsByGroupId.get(item.id) ?? []}
                      selectedListIds={selectedListIds}
                      listTotalsById={listTotalsById}
                      onToggleGroupSelection={(gid) =>
                        toggleFilterSelection(GROUP_CATEGORY, gid)
                      }
                      onToggleGroupExpansion={toggleGroupExpansion}
                      onToggleListSelection={(lid) =>
                        toggleFilterSelection(CATEGORY, lid)
                      }
                    />
                  ) : (
                    <FilterComponentItem
                      key={`list-${item.id}`}
                      title={item.label}
                      count={listTotalsById.get(item.id)}
                      toggleFilter={() =>
                        toggleFilterSelection(CATEGORY, item.id)
                      }
                      tabIndex={0}
                      selected={selectedListIds.has(item.id)}
                      className="px-0"
                    />
                  ),
                )}
                {hasMoreTopLevel && (
                  <ShowMoreButton
                    isExpanded={false}
                    onToggle={() =>
                      setVisibleTopCount((p) =>
                        Math.min(p + TOP_LEVEL_PAGE_SIZE, totalTopLevel),
                      )
                    }
                  />
                )}
                {canShowLessTopLevel && (
                  <ShowMoreButton
                    isExpanded={true}
                    onToggle={() => setVisibleTopCount(TOP_LEVEL_PAGE_SIZE)}
                  />
                )}
                {searchTerm && visibleItems.length === 0 && (
                  <p className="text-[10px] text-slate-500 ml-2">
                    No media lists found
                  </p>
                )}
              </div>
            </>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

PublisherMediaListFilterContainer.displayName =
  "PublisherMediaListFilterContainer";

export default PublisherMediaListFilterContainer;
